import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Injector, Input, OnChanges, OnInit, Output, ViewChild, ViewContainerRef} from "@angular/core";
import {MoveableManagerInterface, NgxMoveableComponent, Renderer} from "ngx-moveable";
import {actionCRUDScene, actionUpdateSceneItem} from "../../../store/actions/user.actions";
import {Id, IPlayerProps, IUser} from "../../../interfaces";
import {ContainerService, SceneService, StoreSelectorsService} from "../../../services";
import {OnDrag, OnRotate, RectInfo} from "moveable";
import {combineLatest, forkJoin, Observable, of, Subject, switchMap} from 'rxjs';
import {debounceTime, delay, finalize, map, take, tap} from 'rxjs/operators';
import {ToastrService} from "ngx-toastr";
import {MAGIC_DIFF} from "../scene-blocks/block-constants";
import {TranslocoService} from "@ngneat/transloco";
import {IPlayer, IPlayerService, IScene, IPlayerObject} from '@signage/lib';
import {Compbaser} from "../../../components/compbaser";
import {Memoizee} from "../../../decorators/memoizee-decorator";
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {isNullOrUndefOrEmpty} from "../../../common";

@Component({
    selector: 'app-scene-moveable',
    templateUrl: './scene-moveable.component.html',
    styleUrls: ['./scene-moveable.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SceneMoveableComponent extends Compbaser implements OnInit, OnChanges {

    @ViewChild('component') component;
    @ViewChild('playercontainer', {read: ViewContainerRef}) entry: ViewContainerRef;
    @ViewChild("moveable") moveable: NgxMoveableComponent;

    readonly magicSize = MAGIC_DIFF;
    private subChange = new Subject<any>();

    user: IUser;
    player: IPlayer;
    playerService: IPlayerService;
    isFullFeatures: boolean;
    sceneData: IPlayerProps;
    editorWidth: number;
    editorHeight: number;
    targets = [];
    elRef: any;
    zoom: any = 1;
    scrollContainer;
    isMoveable = true;
    selectedPlayerIds;
    bounds = {left: 0, top: 0, right: 2000, bottom: 1200};
    guidelines = {horizontal: [], vertical: []};
    selectedSceneId: string;
    initialLoad = true;
    frames;
    reselectOnZoom = false;
    elementGuideLines = [];
    showDirections = false;

    customButtons = {
        name: "editable",
        props: [],
        events: [],
        render: (moveable: MoveableManagerInterface<any, any>, htmlRender: Renderer) => {
            const rect = moveable.getRect();
            const targets = moveable.getTargets();

            let appendElement = '';
            let addTouchButton = false;

            if (targets && targets.length === 1) {
                let sceneType = '';
                const itemId = targets[0].id;
                const nestedSceneSrc = this.sceneService.checkNestedSceneById(itemId);
                if (!isNullOrUndefOrEmpty(nestedSceneSrc)) {
                    sceneType = 'Nested';
                } else {
                    sceneType = this.sceneService.getBlockNameBySceneId(itemId);
                }
                switch (sceneType) {
                    case 'Nested': {
                        appendElement = htmlRender.createElement("button", {
                            className: "custom-button button-jump",
                            onclick: (e) => {
                                this.customControlAction(e, "jump", nestedSceneSrc);
                            },
                            ontouchstart: (e) => {
                                this.customControlAction(e, "jump", nestedSceneSrc);
                            },
                            onmousedown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        }, []);
                        break;
                    }
                    case 'Scheduler': {
                        appendElement = htmlRender.createElement("button", {
                            className: "custom-button button-jump-sched",
                            onclick: (e) => {
                                this.customControlAction(e, "jumpScheduler");
                            },
                            ontouchstart: (e) => {
                                this.customControlAction(e, "jumpScheduler");
                            },
                            onmousedown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        }, []);
                        break;
                    }
                    case 'Playlist': {
                        appendElement = htmlRender.createElement("button", {
                            className: "custom-button button-add-solid",
                            onclick: (e) => {
                                this.customControlAction(e, "playlistAdd");
                            },
                            ontouchstart: (e) => {
                                this.customControlAction(e, "playlistAdd");
                            },
                            onmousedown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        }, []);
                        break;
                    }
                    case 'Label': {
                        appendElement = htmlRender.createElement("button", {
                            className: "custom-button button-label-edit",
                            onclick: (e) => {
                                this.customControlAction(e, "labelEdit");
                            },
                            ontouchstart: (e) => {
                                this.customControlAction(e, "labelEdit");
                            },
                            onmousedown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        }, []);
                        if (this.slideEditMode) {
                            addTouchButton = true;
                        }
                        break;
                    }
                    case 'RichText': {
                        appendElement = htmlRender.createElement("button", {
                            className: "custom-button button-richtext-edit",
                            onclick: (e) => {
                                this.customControlAction(e, "richTextEdit");
                            },
                            ontouchstart: (e) => {
                                this.customControlAction(e, "richTextEdit");
                            },
                            onmousedown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            },
                        }, []);
                        if (this.slideEditMode) {
                            addTouchButton = true;
                        }
                        break;
                    }
                    case 'Button':
                    case 'Image':
                    case 'Icon': {
                        if (this.slideEditMode) {
                            addTouchButton = true;
                        }
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }

            const defaultButtonCnt = 3;
            const {transX, transY, left, right} = this.getCustomButtonsPosition(rect, defaultButtonCnt + (appendElement ? 1 : 0) + (addTouchButton ? 1 : 0));

            /**
             * to convert see SVG icons:
             * https://www.svgbackgrounds.com/tools/svg-to-css/
             * and set Legacy IE9+ and Basic background-image
             */
            const EditableViewer = moveable.useCSS("div", `
                {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    position: absolute;
                    top: 0px;
                    will-change: transform;
                    transform-origin: 0px 0px;
                }
                .custom-button {
                    cursor: pointer;
                    transition: all .2s ease-in-out;
                    width: 24px;
                    height: 24px;
                    background-size: 18px 18px;
                    background-repeat: no-repeat;
                    background-position: center;
                    background-color: transparent;
                    margin-bottom: 4px;
                    border-radius: 2px;
                    appearance: none;
                    border: 0;
                    z-index: 9999;
                }
                .custom-edit {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' width='100' height='100' viewBox='0 0 32 25'%3E%3Cpath d='M3.968,12.061C1.775,12.061,0,13.835,0,16.027c0,2.192,1.773,3.967,3.968,3.967c2.189,0,3.966-1.772,3.966-3.967 C7.934,13.835,6.157,12.061,3.968,12.061z M16.233,12.061c-2.188,0-3.968,1.773-3.968,3.965c0,2.192,1.778,3.967,3.968,3.967 s3.97-1.772,3.97-3.967C20.201,13.835,18.423,12.061,16.233,12.061z M28.09,12.061c-2.192,0-3.969,1.774-3.969,3.967 c0,2.19,1.774,3.965,3.969,3.965c2.188,0,3.965-1.772,3.965-3.965S30.278,12.061,28.09,12.061z' fill='%23000000'%3E%3C/path%3E%3C/svg%3E");
                }
                .custom-button:hover {
                    transform: scale(1.1);
                }
                .button-jump {
                    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='24px' height='24px' viewBox='0 0 24 24' version='1.1'%3E%3Cg id='surface1'%3E%3Cpath style='fill:none%3Bstroke-width:2%3Bstroke-linecap:round%3Bstroke-linejoin:round%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 14 7 L 16 7 C 18.761719 7 21 9.238281 21 12 C 21 14.761719 18.761719 17 16 17 L 14 17 M 10 7 L 8 7 C 5.238281 7 3 9.238281 3 12 C 3 14.761719 5.238281 17 8 17 L 10 17 M 8 12 L 16 12 '/%3E%3C/g%3E%3C/svg%3E");
                }
                .button-duplicate {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1792' height='1792' id='clone'%3E%3Cpath d='M1664 1632V544q0-13-9.5-22.5T1632 512H544q-13 0-22.5 9.5T512 544v1088q0 13 9.5 22.5t22.5 9.5h1088q13 0 22.5-9.5t9.5-22.5zm128-1088v1088q0 66-47 113t-113 47H544q-66 0-113-47t-47-113V544q0-66 47-113t113-47h1088q66 0 113 47t47 113zm-384-384v160h-128V160q0-13-9.5-22.5T1248 128H160q-13 0-22.5 9.5T128 160v1088q0 13 9.5 22.5t22.5 9.5h160v128H160q-66 0-113-47T0 1248V160Q0 94 47 47T160 0h1088q66 0 113 47t47 113z' fill='%23000000'%3E%3C/path%3E%3C/svg%3E");
                }
                 .button-label-edit-v2 {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' width='100' height='100' viewBox='0 0 62 72'%3E%3Cpath d='M62.829,16.484L47.513,1.171c-1.562-1.563-4.094-1.563-5.657,0L0,43.031V64h20.973l41.856-41.855\tC64.392,20.577,64.392,18.05,62.829,16.484z M18,56H8V46l0.172-0.172l10,10L18,56z' fill='%23000000'%3E%3C/path%3E%3C/svg%3E");
                }
                .button-label-edit {
                    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='24px' height='24px' viewBox='0 0 24 24' version='1.1'%3E%3Cg id='surface1'%3E%3Cpath style='fill:none%3Bstroke-width:2%3Bstroke-linecap:round%3Bstroke-linejoin:round%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 12 3 L 12 12 M 9 12 L 15 12 M 20 6 L 20 3 L 4 3 L 4 6 M 4 18 L 20 18 M 4 18 L 7 21 M 4 18 L 7 15 M 20 18 L 17 21 M 20 18 L 17 15 '/%3E%3C/g%3E%3C/svg%3E");
                }
                .button-richtext-edit {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23000000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'/%3E%3Cpath d='M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z'/%3E%3C/svg%3E");
                }
                .button-jump-sched {
                    background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='24px' height='24px' viewBox='0 0 24 24' version='1.1'%3E%3Cg id='surface1'%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 36.65625 54.447917 L 8.84375 54.447917 C 7.479167 54.447917 6.375 53.354167 6.354167 52 L 6.354167 12.125 C 6.354167 10.75 7.46875 9.625 8.84375 9.625 L 48.677083 9.625 C 50.052083 9.625 51.166667 10.75 51.166667 12.125 L 51.166667 34.520833 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 6.354167 20.625 L 51.166667 20.625 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 16.458333 9.625 L 16.458333 4.625 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 40.416667 9.625 L 40.416667 4.625 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 57.645833 45.4375 C 57.645833 52.302083 52.083333 57.875 45.21875 57.875 C 38.354167 57.875 32.791667 52.302083 32.791667 45.4375 C 32.791667 38.572917 38.354167 33.010417 45.21875 33.010417 C 52.083333 33.010417 57.645833 38.572917 57.645833 45.4375 Z M 57.645833 45.4375 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3Cpath style='fill:none%3Bstroke-width:3%3Bstroke-linecap:butt%3Bstroke-linejoin:miter%3Bstroke:rgb(0%25 0%25 0%25)%3Bstroke-opacity:1%3Bstroke-miterlimit:4%3B' d='M 45.21875 36.697917 L 45.21875 45.822917 L 49.572917 49.15625 ' transform='matrix(0.375 0 0 0.375 0 0)'/%3E%3C/g%3E%3C/svg%3E");
                }
                .button-context {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' width='100' height='100' viewBox='0 0 62 72'%3E%3Cpath d='M62.829,16.484L47.513,1.171c-1.562-1.563-4.094-1.563-5.657,0L0,43.031V64h20.973l41.856-41.855\tC64.392,20.577,64.392,18.05,62.829,16.484z M18,56H8V46l0.172-0.172l10,10L18,56z' fill='%23000000'%3E%3C/path%3E%3C/svg%3E");
                }
                .button-add-solid {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1920' viewBox='-1 8 23 11'%3E%3Cpath d='M4 22h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2zm2-9h3v-3h2v3h3v2h-3v3H9v-3H6v-2z M20 2H8v2h12v12h2V4c0-1.103-.897-2-2-2z' fill='%23000000'%3E%3C/path%3E%3C/svg%3E");
                }
                .button-remove {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='currentColor' d='M8 5a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3h4.25a.75.75 0 1 1 0 1.5H19V18a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V6.5H3.75a.75.75 0 0 1 0-1.5H8zM6.5 6.5V18c0 .83.67 1.5 1.5 1.5h8c.83 0 1.5-.67 1.5-1.5V6.5h-11zm3-1.5h5c0-.83-.67-1.5-1.5-1.5h-2c-.83 0-1.5.67-1.5 1.5zm-.25 4h1.5v8h-1.5V9zm4 0h1.5v8h-1.5V9z'%3E%3C/path%3E%3C/svg%3E");
                }
                .button-touch {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 640'%3E%3C!--!Font Awesome Free v7.0.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--%3E%3Cpath d='M320 64C284.7 64 256 92.7 256 128L256 248.5C253.4 248.2 250.7 248 248 248C212.7 248 184 276.7 184 312L184 322.8C181.6 324.7 179.3 326.7 177.1 328.7L156.8 347.2C138.5 363.9 128 387.5 128 412.3L128 422.5C128 476.6 156.7 526.6 203.4 553.8L206 555.3C229.2 568.9 255.7 576 282.6 576L408 576C474.3 576 528 522.3 528 456L528 352C528 316.7 499.3 288 464 288C458.7 288 453.5 288.7 448.5 289.9C437.7 269.7 416.5 256 392 256C389.3 256 386.6 256.2 384 256.5L384 128C384 92.7 355.3 64 320 64zM304 128C304 119.2 311.2 112 320 112C328.8 112 336 119.2 336 128L336 296C336 306.3 342.6 315.5 352.4 318.8C362.2 322.1 373 318.7 379.2 310.5C382.2 306.6 386.8 304.1 392 304.1C400.8 304.1 408 311.3 408 320.1L408 328.1C408 338.4 414.6 347.6 424.4 350.9C434.2 354.2 445 350.8 451.2 342.6C454.2 338.7 458.8 336.2 464 336.2C472.8 336.2 480 343.4 480 352.2L480 456.2C480 496 447.8 528.2 408 528.2L282.6 528.2C264.2 528.2 246.1 523.3 230.2 514C218.5 534.1 218.5 534 230.2 514L227.6 512.5C195.6 493.7 176 459.5 176 422.5L176 412.3C176 401 180.8 390.3 189.1 382.7L200 372.8L200 408C200 416.8 207.2 424 216 424C224.8 424 232 416.8 232 408L232 312C232 303.2 239.2 296 248 296C253.2 296 257.9 298.5 260.8 302.4C267 310.7 277.8 314 287.6 310.7C297.4 307.4 304 298.3 304 288L304 128z'/%3E%3C/svg%3E");
                }
            `);

            return htmlRender.createElement(EditableViewer, {
                key: "editable-viewer",
                className: "moveable-editable",
                style: {
                    transform: `translate(${transX}px, ${transY}px) rotate(${rect.rotation}deg)`,
                    left: left,
                    right: right
                }
            }, [
                addTouchButton ? (
                    htmlRender.createElement("button", {
                        className: "custom-button button-touch",
                        onclick: (e) => {
                            this.customControlAction(e, "touchAction");
                        },
                        ontouchstart: (e) => {
                            this.customControlAction(e, "touchAction");
                        },
                        onmousedown: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        },
                    }, [])
                ) : '',
                appendElement,
                htmlRender.createElement("button", {
                    className: "custom-button button-duplicate",
                    onclick: (e) => {
                        this.customControlAction(e, "duplicate");
                    },
                    ontouchstart: (e) => {
                        this.customControlAction(e, "duplicate");
                    },
                    onmousedown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    },
                }, []),
                htmlRender.createElement("button", {
                    className: "custom-button button-remove",
                    onclick: (e) => {
                        this.customControlAction(e, "remove");
                    },
                    ontouchstart: (e) => {
                        this.customControlAction(e, "remove");
                    },
                    onmousedown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    },
                }, []),
                htmlRender.createElement("button", {
                    className: "custom-button custom-edit",
                    onClick: (e) => {
                        this.customControlAction(e, "edit");
                    },
                    ontouchstart: (e) => {
                        this.customControlAction(e, "edit");
                    },
                    onmousedown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }, []),
            ]);
        }
    } as const;

    @Input()
    set setSelectedSceneId(i_setSelectedSceneId: Id) {
        this.selectedSceneId = i_setSelectedSceneId;
    }

    @Input()
    set setGuidelines(i_guidelines) {
        this.guidelines = i_guidelines;
    }

    @Input()
    set setEditorWidth(i_width) {
        this.editorWidth = i_width;
        this.bounds.right = i_width * (this.zoom || 1);
    }

    @Input()
    set setEditorHeight(i_height) {
        this.editorHeight = i_height;
        this.bounds.bottom = i_height * (this.zoom || 1);
    }

    @Input()
    set setShowDirections(i_value) {
        this.showDirections = i_value;
        this.toggleDirections();
    }

    @Input() changeSelect: boolean;
    @Input() slideEditMode: boolean = false;
    @Input() resolution = 1;

    @Output() updateSceneItems: EventEmitter<any> = new EventEmitter<any>();
    @Output() sceneLoaded: EventEmitter<any> = new EventEmitter<any>();
    @Output() customAction: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        private injector: Injector,
        private containerService: ContainerService,
        private toastr: ToastrService,
        private elementRef: ElementRef,
        private translocoService: TranslocoService,
        private storeSelector: StoreSelectorsService,
        private sceneService: SceneService,
        private cd: ChangeDetectorRef,
        protected override dialog: MatDialog,
        private router: Router
    ) {
        super(dialog);
        this.frames = {};
    }

    ngOnInit() {
        this.playerService = this.injector.get('playerService');
        this.scrollContainer = document.querySelector(".scene-container");
        this.elRef = this.elementRef;

        this.cod(
            // bug here on pro@ms.com scene id: 27
            this.init().subscribe()
        );

        // const dialogRef = this.dialog.open(LoadingSceneComponent, {
        //     height: '400px',
        //     width: '400px',
        //     panelClass: 'transparentDialog',
        //     data: {},
        //     disableClose: true
        // });
        // dialogRef.afterClosed().subscribe(v => {
        //     if (v) {
        //         this.containerService.setMarkedTableRow(this.selectedSceneId);
        //         this.router.navigateByUrl('/editor/list');
        //     }
        // })
    }

    ngOnChanges(changes: import("@angular/core").SimpleChanges): void {
        if (changes.changeSelect && !changes.changeSelect.isFirstChange()) {
            this.resetTargets();
        }
    }

    init(): Observable<any> {
        return of('init').pipe(
            delay(1),
            switchMap(r => this.playerService.init()),
            take(1),
            switchMap(r => this.storeSelector.isFullFeatures()),
            take(1),
            switchMap(v => {
                this.isFullFeatures = v;
                if (this.playerService && this.playerService.setDevMode) {
                    this.playerService.setDevMode(this.isFullFeatures);
                }
                return this.storeSelector.getUser();
            }),
            take(1),
            switchMap(user => {
                this.user = user;
                this.playerService.businessDomain = this.user.businessDomain;
                this.playerService.businessId = Number(this.user.businessId);
                return forkJoin([this.playerUpdate(), this.zoomUpdate(), this.studioChannel(), this.resetMoveable(), this.updateLabel()]);
            }),
            finalize(() => {
                if (this.player != null) {
                    this.playerService.removeScene(this.entry, this.player);
                    this.playerService = null;
                    this.player = null;
                }
            })
        );
    }

    playerUpdate(): Observable<any> {
        return new Observable<any>(sub => {
            if (this.playerService != null) {
                // this.pixiComponent.nativeElement.appendChild(this.playerService.pixi.view);
                this.player = this.playerService.createScene(this.entry, null, true);
            }
            sub.next(1);
            sub.complete();
        }).pipe(
            switchMap(r1 => {
                return this.updateDbService();
            }),
            switchMap(r1 => {
                return this.storeSelector.listenCRUDScene();
            }),
            switchMap(crudScene => {
                /* Add, Update, Delete component */
                if (crudScene != null && this.player != null && crudScene.sceneId == this.selectedSceneId) {
                    return (this.player as IScene).update(<any>this.sceneData.Player, crudScene.command, crudScene.itemIds).pipe(
                        tap({
                            next: value => {
                                // console.log('Component updated');
                                if (value == 'update-ready') {
                                    if (['add', 'delete', 'rebuild', 'update'].indexOf(crudScene.command) > -1) {
                                        this.sceneLoaded.emit({
                                            created: false,
                                            updated: true
                                        });

                                        if (crudScene.command == 'update' && this.moveable && this.slideEditMode) {
                                            this.moveable.updateRect();
                                        }
                                    }
                                }

                                this.cd.markForCheck();
                            },
                            complete: () => {
                                // this.playerService.pixi.renderer.resize(this.player.actualWidth, this.player.actualHeight);
                                this.storeSelector.dispatch(actionCRUDScene({
                                    data: null
                                }));
                            }
                        })
                    );
                }
                return of(null);
            })
        );
    }

    zoomUpdate(): Observable<any> {
        return this.containerService.zoom.pipe(
            switchMap(z => {
                if (z) {
                    this.zoom = z;
                    const root_scene = document.querySelector("#viewSceneContainer .root-scene");

                    if (!root_scene || !root_scene['style']) {
                        return;
                    }
                    root_scene['style']['transform'] = `scale(${z})`;
                    this.bounds.right = this.editorWidth * z;
                    this.bounds.bottom = this.editorHeight * z;
                    this.cd.markForCheck();

                    return this.refreshTargets();
                }
                return of(null);
            })
        );
    }


    updateDbService(): Observable<any> {
        // const $sceneData = this.storeSelector.listenSceneData(this.selectedSceneId);
        const $resources = this.storeSelector.getResources(false);
        const $scenes = this.storeSelector.getScenes(null, true);
        const $transitionPools = this.storeSelector.getTransitionPools();
        const $transitionPoolItems = this.storeSelector.getTransitionItems();
        const $schedules = this.storeSelector.getSchedules();


        return combineLatest([/*$sceneData,*/ $resources, $scenes, $transitionPools, $transitionPoolItems, $schedules]).pipe(
            map(([/*i_sceneData,*/ i_resources, i_scenes, i_transitionPools, i_transitionPoolItems, i_schedules]) => ({
                /*i_sceneData,*/
                i_resources,
                i_scenes,
                i_transitionPools,
                i_transitionPoolItems,
                i_schedules
            })),
            switchMap(v => {
                if (!v /*|| !v.i_sceneData*/ || !v.i_resources) return;

                //??? this.sceneData = v.i_sceneData;

                /** Load dynamic scene component first time */

                v.i_scenes.forEach((_scene) => {
                    const sceneId = _scene.Player.$.id;
                    if (sceneId == this.selectedSceneId) {
                        this.sceneData = _scene;
                    }
                    this.playerService.caching.setRecord('scenes', Number(sceneId),
                        of({
                            json: _scene,
                            changelist_id: _scene.Player.$.changelist_id
                        })
                    );
                });


                v.i_resources.forEach(resource => {
                    this.playerService.caching.setRecord('resources', Number(resource.resource_id), of(resource));
                });

                v.i_schedules.forEach(schedule => {
                    this.playerService.caching.setRecord('schedules', Number(schedule.schedule_id), of(schedule));
                });


                const transitionPools = {};
                v.i_transitionPools.forEach((transitionPool) => {
                    this.playerService.caching.setRecord('transitionPools', Number(transitionPool.id),
                        of({
                            changelist_id: transitionPool.changelist_id,
                            items: []
                        }));
                });
                if (this.initialLoad) {
                    this.initialLoad = false;
                    // console.log('initialLoad: rebuild');
                    return (this.player as IScene).update(<any>this.sceneData.Player, 'rebuild', []).pipe(
                        tap({
                            next: value => {
                                // console.log('load: next=', value);
                                if (value == 'update-ready') {
                                    if (this.playerService != null) {
                                        // this.playerService.pixi.renderer.resize(this.player.actualWidth, this.player.actualHeight);

                                        this.sceneLoaded.emit({
                                            created: true,
                                            updated: false
                                        });
                                        this.cd.markForCheck();
                                    }
                                }
                            }
                        })
                    );
                }
                return of(null);
            })
        );
    }

    studioChannel(): Observable<any> {
        return this.playerService.studioChannel$.pipe(
            tap(v => {
                const event: any = v;
                if (event) {
                    if (event.event == 'input') {
                        this.subChange.next(event);
                    } else if (event.event == 'size') {
                        this.storeSelector.dispatch(actionUpdateSceneItem({
                            sceneId: this.selectedSceneId,
                            sceneItemId: event.playerId,
                            values: [{
                                key: 'width',
                                value: event.width,
                                storePath: 'Data[0].Layout[0].$'
                            },
                                {
                                    key: 'height',
                                    value: event.height,
                                    storePath: 'Data[0].Layout[0].$'
                                }],
                            disableCRUD: true
                        }));
                    }
                    this.cd.detectChanges();
                }
            })
        );
    }

    resetMoveable(): Observable<any> {
        return this.containerService.resetMoveable.pipe(
            switchMap(status => {
                if (status) {
                    return this.refreshTargets();
                }
                return of(null);
            })
        );
    }

    updateLabel(): Observable<any> {
        return this.subChange.pipe(
            debounceTime(333),
            tap(event => {
                if (event != null) {
                    let label_text;
                    if (event.text) {
                        label_text = event.text.replaceAll("'", "`");
                    } else {
                        label_text = event?.text;
                    }
                    this.storeSelector.dispatch(actionUpdateSceneItem({
                        sceneId: this.selectedSceneId,
                        sceneItemId: event.playerId,
                        values: [{
                            key: 'Text',
                            value: [label_text],
                            storePath: 'Data[0].Label[0]'
                        }],
                        disableCRUD: true
                    }));
                }
            })
        );
    }

    @Memoizee({})
    getGuideliness(lines, zoom) {
        // update guide lines
        if (!lines || !zoom) return [];
        return lines.map((v) => v * this.zoom);
    }

    // @Memoizee({})
    // getHorizontalGuides(lines, zoom) {
    //     console.log("horizontal: " + this.zoom);
    //     if (!this.guidelines.horizontal || !this.zoom) return [];
    //     return this.guidelines.horizontal.map((v) => v * this.zoom);
    // }

    private resetTargets(target = null) {
        if (target) {
            this.targets = [target];
        } else {
            this.targets = [].slice.call(document.querySelectorAll("#viewSceneContainer .moveable.ds-selected"));
        }

        this.toggleDirections();

        // if (this.slideEditMode) {
        //     console.log('===============updateTarget');
        //     this.moveable.updateRect();
        // }

        // this.checkTargetLock();
        this.resetFrame();
        this.cd.markForCheck();
    }

    private refreshTargets(): Observable<any> {
        return of('refreshTargets').pipe(
            tap(r => {
                if (this.moveable && (<any> this.moveable.target).length > 0) {
                    this.moveable.updateTarget();
                }
            }),
            delay(1),
            tap(r => {
                this.moveable.updateRect();
            })
        );
        // this.bounds.right = this.editorWidth * this.zoom;
        // this.bounds.bottom = this.editorHeight * this.zoom;
    }

    private resetFrame() {
        if (!this.frames) this.frames = {};
        this.targets = this.targets.filter((t) => {
            if (!t.id) return false;

            if (!this.frames[t.id]) {
                this.frames[t.id] = {
                    translate: [0, 0],
                    rotate: 0,
                };
            }
            return true;
        });
    }

    private toggleDirections() {
        if (this.showDirections) {
            this.elementGuideLines = [].slice.call(document.querySelectorAll("#viewSceneContainer .moveable.ds-selectable:not(.ds-selected)"));
        } else {
            this.elementGuideLines = [];
        }
    }

    getScrollPosition({scrollContainer}) {
        return [scrollContainer.scrollLeft, scrollContainer.scrollTop];
    }

    onScroll({scrollContainer, direction}) {
        scrollContainer.scrollLeft += direction[0] * 20;
        scrollContainer.scrollTop += direction[1] * 20;
    }

    onDrag({target, clientX, clientY, top, left, isPinch}: OnDrag) {
        target.style.setProperty("left", `${left}px`);
        target.style.setProperty("top", `${top}px`);
        // if (this.player != null) {
        //   (this.player as IScene).onDrag({id: target.id, x: left, y: top});
        // }
    }

    onDragEnd({target, isDrag, clientX, clientY, currentTarget, datas}) {
        // console.log("onDragEnd");

        this.updateSceneItems.emit({
            targets: this.targets,
            group: false
        });
    }

    onDragGroup({targets, events}) {
        events.forEach(({target, clientX, clientY, top, left, isPinch}: OnDrag, i) => {
            // const frame = this.frames[i];
            // frame.translate = beforeTranslate;
            if (!target && !target?.style) {
                return;
            }
            target.style.setProperty("left", `${left}px`);
            target.style.setProperty("top", `${top}px`);
            // if (this.player != null) {
            //   (this.player as IScene).onDrag({id: target.id, x: left, y: top});
            // }
        });
    }

    onDragGroupEnd({targets, isDrag, clientX, clientY}) {
        // console.log("onDragGroupEnd");
        this.updateSceneItems.emit({
            targets: this.targets,
            group: true
        });
    }

    onResizeStart(e) {
        // e.setOrigin(["%", "%"]);
        // e.dragStart && e.dragStart.set(this.frames[e.target.id].translate);
    }

    onResize(e) {
        // const beforeTranslate = e.drag.beforeTranslate;

        // this.frames[e.target.id].translate = beforeTranslate;
        e.target.style.width = `${e.width}px`;
        e.target.style.height = `${e.height}px`;

        e.target.style.setProperty("left", `${e.drag.left}px`);
        e.target.style.setProperty("top", `${e.drag.top}px`);

        // console.log(e.drag.left + " === " + e.drag.top);
        // console.log(beforeTranslate[0] + " === " + beforeTranslate[1]);

        // e.target.style.transform = `translate(${beforeTranslate[0]}px, ${beforeTranslate[1]}px)`;
    }

    // onResize({ target, clientX, clientY, width, height, drag }: OnResize) {
    //     target.style.setProperty("width", `${width}px`);
    //     target.style.setProperty("height", `${height}px`);
    //     target.style.setProperty("left", `${drag.left}px`);
    //     target.style.setProperty("top", `${drag.top}px`);

    //     // this.setTransform(target);
    // }

    onResizeEnd({target, lastEvent}) {
        // if (!lastEvent) return;

        // const beforeTranslate = lastEvent.drag.beforeTranslate;
        // target.style.left = `${lastEvent.drag.left}px`;
        // target.style.top =  `${lastEvent.drag.top}px`;
        // target.style.transform = `translate(0, 0)`;

        this.updateSceneItems.emit({
            targets: this.targets,
            group: false
        });
    }

    // onResizeGroup({ events }) {
    //     events.forEach(({ target, clientX, clientY, width, height, drag }: OnResize, i) => {
    //         // const frame = this.frames[i];
    //         target.style.setProperty("width", `${width}px`);
    //         target.style.setProperty("height", `${height}px`);
    //         target.style.setProperty("left", `${drag.left - drag.beforeTranslate[0]}px`);
    //         target.style.setProperty("top", `${drag.top - drag.beforeTranslate[1]}px`);
    //     });
    // }

    onResizeGroupStart({events}) {
        // events.forEach((ev) => {
        //     ev.dragStart && ev.dragStart.set(this.frames[ev.target.id].translate);
        // });
    }

    onResizeGroup({events}) {
        events.forEach((ev, i) => {
            this.frames[ev.target.id].translate = ev.drag.beforeTranslate;
            ev.target.style.width = `${ev.width}px`;
            ev.target.style.height = `${ev.height}px`;
            ev.target.style.left = `${ev.drag.left}px`;
            ev.target.style.top = `${ev.drag.top}px`;
            // ev.target.style.transform
            //     = `translate(${ev.drag.beforeTranslate[0]}px, ${ev.drag.beforeTranslate[1]}px)`;
        });
    }

    onResizeGroupEnd({targets, isDrag, clientX, clientY}) {
        // console.log("onResizeGroupEnd");
        this.updateSceneItems.emit({
            targets: this.targets,
            group: true
        });
    }

    onRotate({target, clientX, clientY, beforeDelta, isPinch, beforeRotate}: OnRotate) {
        const deg = this.getRotate(target.style) + beforeDelta;
        target.style.setProperty("transform", `rotate(${deg}deg)`);
        // this.setTransform(target);
    }

    onRotateEnd({target, isDrag, clientX, clientY}) {
        this.updateSceneItems.emit({
            targets: this.targets,
            group: false
        });
    }

    onRotateGroup({events}) {
        events.forEach(({target, clientX, clientY, beforeDelta, isPinch, drag}, i) => {
            const deg = this.getRotate(target.style) + beforeDelta;

            // get drag event
            // frame.translate = drag.beforeTranslate;
            target.style.transform
                = `translate(${drag.beforeTranslate[0]}px, ${drag.beforeTranslate[1]}px) `
                + `rotate(${deg}deg)`;
        });
    }

    onRotateGroupEnd({targets, isDrag, clientX, clientY}) {
        // console.log("onRotateGroupEnd", targets, isDrag);
        this.updateSceneItems.emit({
            targets: this.targets,
            group: true,
            isRotated: true
        });
    }

    // private checkTargetLock() {
    //   let isLocked = false;
    //   this.isMoveable = true;
    //   if (this.targets && this.targets.length > 0) {
    //     this.targets.forEach((t) => {
    //       if (t.className.includes("locked")) {
    //         isLocked = true;
    //       }
    //     });
    //   }
    //   if (isLocked) {
    //     if (this.isMoveable) {
    //       // this.toastr.clear();
    //       this.toastr.warning(this.translocoService.translate('toast.cantDrag', {}, this.translocoService.getActiveLang()));
    //     }
    //     this.isMoveable = false;
    //   } else {
    //     this.isMoveable = true;
    //   }
    // }

    clickVideo(e) {
        if (e && e.target) {
            setTimeout(() => {
                const t = e.target;
                t.paused ? t.play() : t.pause();
            });
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    }

    private customControlAction(event, action, nestedSceneSrc = null) {
        event.preventDefault();
        event.stopPropagation();
        this.customAction.emit({
            action,
            clientX: event.clientX,
            clientY: event.clientY,
            nestedSceneSrc
        });
    }

    private getRotate(style) {
        let angle = 0;
        let tr = style.getPropertyValue("-webkit-transform");
        if (tr !== "none" && tr.indexOf("rotate") > -1) {

            let ang = /rotate\((.+)deg\)/i.exec(tr);
            angle = parseInt(ang[1]);
        }
        return angle;
    }

    exitScene() {
        this.containerService.setMarkedTableRow(this.selectedSceneId);
        this.router.navigateByUrl('/editor/list');
    }

    private getCustomButtonsPosition(rect: RectInfo, btnCnt = 0) {
        const customButtonsWidth = btnCnt * 32;
        let transX = 0;
        let transY = 0;
        let left = 'unset';
        let right = 'unset';

        let alignX = 'center';
        let alignY = 'bottom';

        if (customButtonsWidth > rect.width) {
            if (rect.left < (customButtonsWidth - rect.width) / 2) {
                alignX = 'left';
            } else if ((this.editorWidth * this.zoom - rect.left) < (customButtonsWidth + rect.width) / 2) {
                alignX = 'right';
            }
        }

        if (this.editorHeight * this.zoom - rect.top - rect.height < 50) {
            alignY = 'top';
        }

        // Set position
        if (alignX == 'center') {
            transX = (rect.width / 2) - (customButtonsWidth / 2);
            left = '0px';
        } else if (alignX == 'left') {
            transX = 0;
            left = '0px';
        } else if (alignX == 'right') {
            transX = rect.width;
            right = '100%';
        }

        if (alignY == 'bottom') {
            transY = rect.height + 10;
        } else if (alignY == 'top') {
            transY = -50;
        }

        return {
            transX,
            transY,
            left,
            right
        }
    }

    override destroy(): void {
        this.elementGuideLines = [];
        this.targets = [];
        this.moveable.target = [];
        this.frames = {};
        super.destroy();
    }
}
